/**
 * Shared analytics filters, kept in the URL.
 *
 * Putting them in the query string is the main thing a website gives you over
 * the app: a filtered view can be bookmarked, or pasted to someone else, and
 * the back button works.
 */
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { defaultRange } from './dates'

export type Scope = 'all' | 'fulfilled'
export type Compare = 'none' | 'previous_period' | 'previous_year'

export interface Filters {
  from: string
  to: string
  outletId: number | null
  scope: Scope
  compare: Compare
}

/** Filters on a page that defaults to all time: a missing date is an open end. */
export interface OpenFilters extends Omit<Filters, 'from' | 'to'> {
  from: string | null
  to: string | null
}

/** A change to the filters; null clears a date (all-time pages only). */
export type FilterPatch = Partial<Omit<Filters, 'from' | 'to'>> & {
  from?: string | null
  to?: string | null
}

const SCOPES: Scope[] = ['all', 'fulfilled']
const COMPARES: Compare[] = ['none', 'previous_period', 'previous_year']

/**
 * The product pages pass allTime: someone looking up a product wants all of
 * its sales, not the last 30 days, so no date in the URL means no bound.
 * Everywhere else it means the default range.
 */
export function useFilters(): { filters: Filters; update: (patch: FilterPatch) => void }
export function useFilters(options: {
  allTime?: boolean
}): { filters: OpenFilters; update: (patch: FilterPatch) => void }
export function useFilters(options?: { allTime?: boolean }) {
  const [params, setParams] = useSearchParams()
  const allTime = options?.allTime ?? false

  const filters = useMemo<OpenFilters>(() => {
    const fallback = allTime ? { from: null, to: null } : defaultRange()
    const outlet = params.get('outlet')
    const scope = params.get('scope') as Scope | null
    const compare = params.get('compare') as Compare | null
    return {
      from: params.get('from') ?? fallback.from,
      to: params.get('to') ?? fallback.to,
      outletId: outlet ? Number(outlet) : null,
      scope: scope && SCOPES.includes(scope) ? scope : 'all',
      compare: compare && COMPARES.includes(compare) ? compare : 'previous_period',
    }
  }, [params, allTime])

  const update = useCallback(
    (patch: FilterPatch) => {
      const next = new URLSearchParams(params)
      if (patch.from === null) next.delete('from')
      else if (patch.from !== undefined) next.set('from', patch.from)
      if (patch.to === null) next.delete('to')
      else if (patch.to !== undefined) next.set('to', patch.to)
      if (patch.scope !== undefined) next.set('scope', patch.scope)
      if (patch.compare !== undefined) next.set('compare', patch.compare)
      if (patch.outletId !== undefined) {
        if (patch.outletId === null) next.delete('outlet')
        else next.set('outlet', String(patch.outletId))
      }
      // A filter change always means page one again.
      next.delete('offset')
      setParams(next, { replace: true })
    },
    [params, setParams],
  )

  return { filters, update }
}

/**
 * Paging lives in the URL too, so a deep link lands on the right page. `key`
 * names the offset for a second list on a page whose URL already carries one.
 */
export function usePaging(defaultLimit = 25, key = 'offset') {
  const [params, setParams] = useSearchParams()
  const offset = Math.max(0, Number(params.get(key) ?? 0) || 0)
  const limit = Number(params.get('limit') ?? defaultLimit) || defaultLimit

  const setOffset = useCallback(
    (next: number) => {
      const updated = new URLSearchParams(params)
      if (next <= 0) updated.delete(key)
      else updated.set(key, String(next))
      setParams(updated, { replace: true })
    },
    [params, setParams, key],
  )

  return { offset, limit, setOffset }
}

/** A single query-string value, for per-page controls like sort or segment. */
export function useParam<T extends string>(key: string, fallback: T, allowed?: readonly T[]) {
  const [params, setParams] = useSearchParams()
  const raw = params.get(key) as T | null
  const value = raw && (!allowed || allowed.includes(raw)) ? raw : fallback

  const set = useCallback(
    (next: T) => {
      const updated = new URLSearchParams(params)
      if (next === fallback) updated.delete(key)
      else updated.set(key, next)
      updated.delete('offset')
      setParams(updated, { replace: true })
    },
    [params, setParams, key, fallback],
  )

  return [value, set] as const
}
